import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import TurnoModal from './TurnoModal';
import { eliminarTurno, listarTurnos, TurnoResponse } from '../api/turnoApi';
import styles from './TurnoCalendarModal.module.css';

interface TurnoCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NOMBRES_DIA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

function mondayOf(date: Date): Date {
  const d = new Date(date);
  const dia = d.getDay();
  const diff = dia === 0 ? -6 : 1 - dia;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, dias: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + dias);
  return d;
}

function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatFecha(d: Date): string {
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

function formatHora(hora: string): string {
  return hora.slice(0, 5);
}

export default function TurnoCalendarModal({ isOpen, onClose }: TurnoCalendarModalProps) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [turnosByDay, setTurnosByDay] = useState<Record<string, TurnoResponse[]>>({});
  const [loading, setLoading] = useState(false);
  const [turnoModalOpen, setTurnoModalOpen] = useState(false);
  const [editingTurno, setEditingTurno] = useState<TurnoResponse | null>(null);
  const [confirmDeleteTurno, setConfirmDeleteTurno] = useState<TurnoResponse | null>(null);
  const [deletingTurnoId, setDeletingTurnoId] = useState<string | null>(null);

  const dias = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));

  useEffect(() => {
    if (!isOpen) return;
    let cancelado = false;
    setLoading(true);
    Promise.all(dias.map((d) => listarTurnos(toISODate(d))))
      .then((resultados) => {
        if (cancelado) return;
        const mapa: Record<string, TurnoResponse[]> = {};
        dias.forEach((d, i) => { mapa[toISODate(d)] = resultados[i]; });
        setTurnosByDay(mapa);
      })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, weekStart.getTime()]);

  function handleTurnoGuardado(turno: TurnoResponse) {
    setTurnosByDay((prev) => {
      const next: Record<string, TurnoResponse[]> = {};
      for (const [dia, lista] of Object.entries(prev)) {
        next[dia] = lista.filter((t) => t.id !== turno.id);
      }
      if (turno.fecha in next) {
        next[turno.fecha] = [...next[turno.fecha], turno].sort((a, b) => a.hora.localeCompare(b.hora));
      }
      return next;
    });
  }

  function openCreateModal() {
    setEditingTurno(null);
    setTurnoModalOpen(true);
  }

  function openEditModal(turno: TurnoResponse) {
    setEditingTurno(turno);
    setTurnoModalOpen(true);
  }

  function closeTurnoModal() {
    setTurnoModalOpen(false);
    setEditingTurno(null);
  }

  async function confirmarEliminarTurno() {
    if (!confirmDeleteTurno) return;
    const id = confirmDeleteTurno.id;
    const fecha = confirmDeleteTurno.fecha;
    setConfirmDeleteTurno(null);
    setDeletingTurnoId(id);
    try {
      await eliminarTurno(id);
      setTurnosByDay((prev) => {
        if (!(fecha in prev)) return prev;
        return { ...prev, [fecha]: prev[fecha].filter((t) => t.id !== id) };
      });
    } finally {
      setDeletingTurnoId(null);
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Calendario de turnos" width={960}>
        <div className={styles.toolbar}>
          <div className={styles.weekNav}>
            <button className={styles.navBtn} onClick={() => setWeekStart((prev) => addDays(prev, -7))}>
              ← Semana anterior
            </button>
            <span className={styles.weekLabel}>
              {formatFecha(dias[0])} – {formatFecha(dias[4])}
            </span>
            <button className={styles.navBtn} onClick={() => setWeekStart((prev) => addDays(prev, 7))}>
              Semana siguiente →
            </button>
          </div>
          <button className={styles.addBtn} onClick={openCreateModal}>
            + Registrar turno
          </button>
        </div>

        {loading ? (
          <p className={styles.loading}>Cargando turnos...</p>
        ) : (
          <div className={styles.grid}>
            {dias.map((d, i) => {
              const iso = toISODate(d);
              const turnos = turnosByDay[iso] ?? [];
              return (
                <div key={iso} className={styles.dayColumn}>
                  <div className={styles.dayHeader}>
                    <span className={styles.dayName}>{NOMBRES_DIA[i]}</span>
                    <span className={styles.dayDate}>{formatFecha(d)}</span>
                  </div>
                  {turnos.length === 0 ? (
                    <p className={styles.dayEmpty}>Sin turnos</p>
                  ) : (
                    <div className={styles.dayList}>
                      {turnos.map((t) => (
                        <div key={t.id} className={styles.dayTurno}>
                          <div className={styles.dayTurnoInfo}>
                            <span className={styles.dayTurnoHora}>{formatHora(t.hora)}</span>
                            <span className={styles.dayTurnoPaciente}>{t.pacienteApellido}, {t.pacienteNombre}</span>
                          </div>
                          <div className={styles.dayTurnoActions}>
                            <button
                              className={styles.dayTurnoEditBtn}
                              onClick={() => openEditModal(t)}
                              title="Editar turno"
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                            <button
                              className={styles.dayTurnoDeleteBtn}
                              onClick={() => setConfirmDeleteTurno(t)}
                              disabled={deletingTurnoId === t.id}
                              title="Eliminar turno"
                            >
                              <i className="bi bi-trash-fill"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      <TurnoModal
        isOpen={turnoModalOpen}
        onClose={closeTurnoModal}
        onSaved={handleTurnoGuardado}
        editingTurno={editingTurno}
      />

      <Modal
        isOpen={confirmDeleteTurno !== null}
        onClose={() => setConfirmDeleteTurno(null)}
        title="Eliminar turno"
        width={420}
      >
        <p className={styles.confirmText}>
          ¿Estás seguro de que querés eliminar el turno de{' '}
          <strong>{confirmDeleteTurno?.pacienteApellido}, {confirmDeleteTurno?.pacienteNombre}</strong> del{' '}
          {confirmDeleteTurno && new Date(`${confirmDeleteTurno.fecha}T00:00:00`).toLocaleDateString('es-AR')}{' '}
          a las {confirmDeleteTurno && formatHora(confirmDeleteTurno.hora)} hs?
          Esta acción no se puede deshacer.
        </p>
        <div className={styles.confirmActions}>
          <button className={styles.cancelConfirmBtn} onClick={() => setConfirmDeleteTurno(null)}>
            Cancelar
          </button>
          <button className={styles.deleteConfirmBtn} onClick={confirmarEliminarTurno}>
            Eliminar
          </button>
        </div>
      </Modal>
    </>
  );
}
